import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, ClipboardList, LogOut, Loader2, Pencil, Trash2, UploadCloud, FileDown,
  Plus, Scissors, AlertTriangle, Eye, X, RotateCcw, Percent, Hash,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import DxfCanvas from "@/components/DxfCanvas";
import { useAuth } from "@/hooks/useAuth";
import api, { errorMessage } from "@/lib/api";
import { ORDER_STATUS_META, FABRIC_STATUS_META, DEFECT_TYPES } from "@/lib/orderStatus";

const fmtPct = (n) => `${Number(n).toFixed(1).replace(".", ",")}%`;
const emptyMatrixRow = () => ({ piece_name: "", size: "-", qty_per_layer: 0 });
const emptyDefectForm = () => ({ type: DEFECT_TYPES[0], customType: "", piece_name: "", size: "", quantity: "1" });

function MetaCell({ icon: Icon, label, value, accent }) {
  return (
    <div className="p-3 border-r border-border last:border-r-0">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className={`mono text-lg font-medium leading-none ${accent ? "text-[hsl(var(--success))]" : "text-foreground"}`}>
        {value}
      </div>
    </div>
  );
}

function FabricCard({
  fabric, onEditMatrix, onRegisterCut, onRegisterDefect, onDeleteDefect,
  onPreview, onDeleteFabric, onStatusChange, onDownload, onAttachClick, onFileChange, registerFileInput,
}) {
  const lastCut = fabric.cut_records[fabric.cut_records.length - 1];
  const pct = fabric.total_pieces > 0 ? Math.min(100, (fabric.cut_pieces / fabric.total_pieces) * 100) : 0;
  return (
    <div className="border border-border bg-card p-4" data-testid={`fabric-card-${fabric.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="font-medium truncate">{fabric.name}</div>
          <div className="text-xs text-muted-foreground mono mt-0.5">
            {fabric.file ? fabric.file.filename : "sem ficheiro"} · {fabric.layers} camadas · {fabric.total_pieces} peças
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Select value={fabric.status} onValueChange={(v) => onStatusChange(fabric.id, v)}>
            <SelectTrigger className="w-32 h-8 text-xs" data-testid={`fabric-status-${fabric.id}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="not_cut">Por cortar</SelectItem>
              <SelectItem value="cutting">Em corte</SelectItem>
              <SelectItem value="done">Cortado</SelectItem>
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" className="rounded-[4px] text-destructive" onClick={() => onDeleteFabric(fabric.id)} data-testid={`fabric-delete-${fabric.id}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      {fabric.total_pieces > 0 && (
        <div className="mt-2 h-1.5 bg-muted rounded-full overflow-hidden">
          <div className="h-full bg-[hsl(var(--success))]" style={{ width: `${pct}%` }} />
        </div>
      )}

      <div className="flex flex-wrap gap-2 mt-3">
        <Button size="sm" variant="outline" className="rounded-[4px]" onClick={() => onEditMatrix(fabric)} data-testid={`fabric-matrix-${fabric.id}`}>
          <Pencil className="w-3.5 h-3.5" /> Matriz
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="rounded-[4px]"
          onClick={() => onRegisterCut(fabric)}
          disabled={fabric.matrix.length === 0 || fabric.layers === 0}
          title={fabric.matrix.length === 0 || fabric.layers === 0 ? "Define primeiro a matriz e o nº de camadas" : undefined}
          data-testid={`fabric-cut-${fabric.id}`}
        >
          <Scissors className="w-3.5 h-3.5" /> Registar corte
        </Button>
        <Button size="sm" variant="outline" className="rounded-[4px]" onClick={() => onRegisterDefect(fabric.id)} data-testid={`fabric-defect-${fabric.id}`}>
          <AlertTriangle className="w-3.5 h-3.5" /> Defeito
        </Button>
        {fabric.file && (
          <Button size="sm" variant="outline" className="rounded-[4px]" onClick={() => onPreview(fabric)} data-testid={`fabric-preview-${fabric.id}`}>
            <Eye className="w-3.5 h-3.5" /> Ver desenho
          </Button>
        )}
        <Button size="sm" variant="outline" className="rounded-[4px]" onClick={() => onAttachClick(fabric.id)} data-testid={`fabric-attach-${fabric.id}`}>
          <UploadCloud className="w-3.5 h-3.5" /> {fabric.file ? "Substituir ficheiro" : "Anexar ficheiro"}
        </Button>
        {fabric.file && (
          <Button size="sm" variant="outline" className="rounded-[4px]" onClick={() => onDownload(fabric)} title="Descarregar ficheiro">
            <FileDown className="w-3.5 h-3.5" />
          </Button>
        )}
        <input
          ref={registerFileInput}
          type="file"
          accept=".dxf,.gbr"
          className="hidden"
          onChange={(e) => onFileChange(fabric.id, e.target.files?.[0])}
        />
      </div>

      {lastCut && (
        <div className="mt-2 text-[11px] text-muted-foreground">
          Cortado: {fabric.cut_pieces}/{fabric.total_pieces} peças · último registo por {lastCut.user_name}
        </div>
      )}

      {fabric.defects.length > 0 && (
        <div className="mt-3 border-t border-border pt-2 space-y-1">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Defeitos</div>
          {fabric.defects.map((d) => (
            <div key={d.id} className="flex items-center justify-between text-xs gap-2">
              <span className="truncate">
                {d.type}
                {d.piece_name ? ` · ${d.piece_name}${d.size && d.size !== "-" ? " " + d.size : ""}` : ""} · qtd {d.quantity} · {d.reported_by_name}
              </span>
              <button onClick={() => onDeleteDefect(fabric.id, d.id)} className="shrink-0 text-muted-foreground hover:text-destructive">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function OrderDetail() {
  const { id } = useParams();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const fileInputs = useRef({});
  const fabricFileInputRef = useRef(null);

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchOrder = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/orders/${id}`);
      setOrder(res.data);
    } catch (e) {
      toast.error("Erro ao carregar ordem", { description: errorMessage(e) });
    } finally {
      setLoading(false);
    }
  }, [id]);
  useEffect(() => { fetchOrder(); }, [fetchOrder]);

  // --- order edit ---
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [savingOrder, setSavingOrder] = useState(false);
  const openEdit = () => {
    setEditForm({ client: order.client, reference: order.reference, due_date: order.due_date });
    setEditOpen(true);
  };
  const saveEdit = async (e) => {
    e.preventDefault();
    setSavingOrder(true);
    try {
      const res = await api.patch(`/orders/${id}`, editForm);
      setOrder(res.data);
      toast.success("Ordem atualizada");
      setEditOpen(false);
    } catch (err) {
      toast.error("Erro ao atualizar ordem", { description: errorMessage(err) });
    } finally {
      setSavingOrder(false);
    }
  };

  // --- delete order ---
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const handleDeleteOrder = async () => {
    setDeleting(true);
    try {
      await api.delete(`/orders/${id}`);
      toast.success("Ordem apagada");
      navigate("/ordens");
    } catch (err) {
      toast.error("Erro ao apagar ordem", { description: errorMessage(err) });
      setDeleting(false);
    }
  };

  // --- add fabric ---
  // The file is parsed immediately on selection (reusing the stateless Fase 1
  // /parse-dxf /parse-gbr endpoints — nothing is created yet) so the operator
  // sees and confirms the matriz + nº de camadas right here, before the
  // tecido even exists, instead of having to open a separate step afterward.
  const [addFabricOpen, setAddFabricOpen] = useState(false);
  const [fabricName, setFabricName] = useState("");
  const [fabricFile, setFabricFile] = useState(null);
  const [fabricLayers, setFabricLayers] = useState("");
  const [fabricMatrix, setFabricMatrix] = useState([]);
  const [parsingFabricFile, setParsingFabricFile] = useState(false);
  const [addingFabric, setAddingFabric] = useState(false);

  const resetAddFabric = () => {
    setAddFabricOpen(false);
    setFabricName("");
    setFabricFile(null);
    setFabricLayers("");
    setFabricMatrix([]);
  };

  const pickFabricFile = async (f) => {
    if (!f) return;
    const lower = f.name.toLowerCase();
    const isDxf = lower.endsWith(".dxf");
    const isGbr = lower.endsWith(".gbr");
    if (!isDxf && !isGbr) {
      toast.error("Formato inválido", { description: "Selecione um ficheiro .dxf ou .gbr." });
      return;
    }
    setFabricFile(f);
    setParsingFabricFile(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await api.post(`/${isDxf ? "parse-dxf" : "parse-gbr"}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setFabricMatrix(res.data.piece_summary.map((s) => ({ piece_name: s.name, size: s.size || "-", qty_per_layer: s.qty })));
    } catch (err) {
      toast.error("Erro ao ler o ficheiro", { description: errorMessage(err) });
      setFabricFile(null);
    } finally {
      setParsingFabricFile(false);
    }
  };
  const removeFabricFile = () => { setFabricFile(null); setFabricMatrix([]); };
  const updateFabricMatrixRow = (idx, field, value) => {
    setFabricMatrix((rows) => rows.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  };
  const addFabricMatrixRow = () => setFabricMatrix((rows) => [...rows, emptyMatrixRow()]);
  const removeFabricMatrixRow = (idx) => setFabricMatrix((rows) => rows.filter((_, i) => i !== idx));

  const submitAddFabric = async (e) => {
    e.preventDefault();
    setAddingFabric(true);
    try {
      let res;
      if (fabricFile) {
        const fd = new FormData();
        fd.append("file", fabricFile);
        fd.append("name", fabricName);
        fd.append("layers", String(Number(fabricLayers) || 0));
        fd.append(
          "matrix",
          JSON.stringify(
            fabricMatrix
              .filter((r) => r.piece_name.trim())
              .map((r) => ({
                piece_name: r.piece_name.trim(),
                size: (r.size || "-").trim() || "-",
                qty_per_layer: Number(r.qty_per_layer) || 0,
              }))
          )
        );
        res = await api.post(`/orders/${id}/fabrics/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      } else {
        res = await api.post(`/orders/${id}/fabrics`, { name: fabricName });
      }
      setOrder(res.data);
      toast.success("Tecido adicionado");
      resetAddFabric();
    } catch (err) {
      toast.error("Erro ao adicionar tecido", { description: errorMessage(err) });
    } finally {
      setAddingFabric(false);
    }
  };

  // --- fabric status quick change ---
  const changeFabricStatus = async (fabricId, statusVal) => {
    try {
      const res = await api.patch(`/orders/${id}/fabrics/${fabricId}`, { status: statusVal });
      setOrder(res.data);
      toast.success("Estado do tecido atualizado");
    } catch (err) {
      toast.error("Erro ao mudar estado", { description: errorMessage(err) });
    }
  };

  // --- delete fabric ---
  const [deleteFabricId, setDeleteFabricId] = useState(null);
  const [deletingFabric, setDeletingFabric] = useState(false);
  const handleDeleteFabric = async () => {
    setDeletingFabric(true);
    try {
      const res = await api.delete(`/orders/${id}/fabrics/${deleteFabricId}`);
      setOrder(res.data);
      toast.success("Tecido removido");
      setDeleteFabricId(null);
    } catch (err) {
      toast.error("Erro ao remover tecido", { description: errorMessage(err) });
    } finally {
      setDeletingFabric(false);
    }
  };

  // --- matrix edit ---
  const [matrixFabricId, setMatrixFabricId] = useState(null);
  const [matrixForm, setMatrixForm] = useState(null);
  const [savingMatrix, setSavingMatrix] = useState(false);
  const [loadingSuggestion, setLoadingSuggestion] = useState(false);
  const openMatrix = (fabric) => {
    setMatrixFabricId(fabric.id);
    setMatrixForm({ name: fabric.name, layers: fabric.layers, matrix: fabric.matrix.map((r) => ({ ...r })) });
  };
  const closeMatrix = () => { setMatrixFabricId(null); setMatrixForm(null); };
  const updateMatrixRow = (idx, field, value) => {
    setMatrixForm((f) => ({ ...f, matrix: f.matrix.map((r, i) => (i === idx ? { ...r, [field]: value } : r)) }));
  };
  const addMatrixRow = () => setMatrixForm((f) => ({ ...f, matrix: [...f.matrix, emptyMatrixRow()] }));
  const removeMatrixRow = (idx) => setMatrixForm((f) => ({ ...f, matrix: f.matrix.filter((_, i) => i !== idx) }));
  const resetFromFile = async () => {
    setLoadingSuggestion(true);
    try {
      const res = await api.get(`/orders/${id}/fabrics/${matrixFabricId}/preview`);
      const matrix = res.data.piece_summary.map((s) => ({ piece_name: s.name, size: s.size || "-", qty_per_layer: s.qty }));
      setMatrixForm((f) => ({ ...f, matrix }));
      toast.success("Sugestão reposta a partir do ficheiro");
    } catch (err) {
      toast.error("Erro ao obter sugestão", { description: errorMessage(err) });
    } finally {
      setLoadingSuggestion(false);
    }
  };
  const saveMatrix = async (e) => {
    e.preventDefault();
    setSavingMatrix(true);
    try {
      const payload = {
        name: matrixForm.name,
        layers: Number(matrixForm.layers),
        matrix: matrixForm.matrix
          .filter((r) => r.piece_name.trim())
          .map((r) => ({
            piece_name: r.piece_name.trim(),
            size: (r.size || "-").trim() || "-",
            qty_per_layer: Number(r.qty_per_layer) || 0,
          })),
      };
      const res = await api.patch(`/orders/${id}/fabrics/${matrixFabricId}`, payload);
      setOrder(res.data);
      toast.success("Matriz guardada");
      closeMatrix();
    } catch (err) {
      toast.error("Erro ao guardar matriz", { description: errorMessage(err) });
    } finally {
      setSavingMatrix(false);
    }
  };

  // --- cut ---
  // One row per matrix entry (modelo × tamanho): "já cortado" / "falta" is
  // derived from the fabric's own cut_records, and "Tudo cortado" just fills
  // every row's input with its own remaining quantity.
  const [cutFabricId, setCutFabricId] = useState(null);
  const [cutRows, setCutRows] = useState([]);
  const [savingCut, setSavingCut] = useState(false);
  const openCut = (fabric) => {
    const already = {};
    for (const rec of fabric.cut_records) {
      for (const item of rec.items) {
        const key = `${item.piece_name}||${item.size}`;
        already[key] = (already[key] || 0) + item.quantity;
      }
    }
    const rows = fabric.matrix.map((r) => {
      const key = `${r.piece_name}||${r.size}`;
      const target = r.qty_per_layer * fabric.layers;
      const alreadyQty = already[key] || 0;
      return {
        piece_name: r.piece_name,
        size: r.size,
        target,
        already: alreadyQty,
        remaining: Math.max(0, target - alreadyQty),
        value: "",
      };
    });
    setCutFabricId(fabric.id);
    setCutRows(rows);
  };
  const updateCutRow = (idx, value) => {
    setCutRows((rows) => rows.map((r, i) => (i === idx ? { ...r, value } : r)));
  };
  const fillAllCut = () => {
    setCutRows((rows) => rows.map((r) => ({ ...r, value: r.remaining > 0 ? String(r.remaining) : "" })));
  };
  const submitCut = async (e) => {
    e.preventDefault();
    const items = cutRows
      .map((r) => ({ piece_name: r.piece_name, size: r.size, quantity: Number(r.value) || 0 }))
      .filter((i) => i.quantity > 0);
    if (items.length === 0) {
      toast.error("Indica pelo menos uma quantidade cortada.");
      return;
    }
    setSavingCut(true);
    try {
      const res = await api.post(`/orders/${id}/fabrics/${cutFabricId}/cut`, { items });
      setOrder(res.data);
      toast.success("Corte registado");
      setCutFabricId(null);
      setCutRows([]);
    } catch (err) {
      toast.error("Erro ao registar corte", { description: errorMessage(err) });
    } finally {
      setSavingCut(false);
    }
  };

  // --- defect ---
  const [defectFabricId, setDefectFabricId] = useState(null);
  const [defectForm, setDefectForm] = useState(emptyDefectForm());
  const [savingDefect, setSavingDefect] = useState(false);
  const openDefect = (fabricId) => { setDefectFabricId(fabricId); setDefectForm(emptyDefectForm()); };
  const submitDefect = async (e) => {
    e.preventDefault();
    setSavingDefect(true);
    try {
      const type = defectForm.type === "Outro" ? defectForm.customType.trim() : defectForm.type;
      const res = await api.post(`/orders/${id}/fabrics/${defectFabricId}/defects`, {
        type,
        piece_name: defectForm.piece_name || null,
        size: defectForm.size || null,
        quantity: Number(defectForm.quantity),
      });
      setOrder(res.data);
      toast.success("Defeito registado");
      setDefectFabricId(null);
    } catch (err) {
      toast.error("Erro ao registar defeito", { description: errorMessage(err) });
    } finally {
      setSavingDefect(false);
    }
  };
  const deleteDefect = async (fabricId, defectId) => {
    try {
      const res = await api.delete(`/orders/${id}/fabrics/${fabricId}/defects/${defectId}`);
      setOrder(res.data);
      toast.success("Defeito removido");
    } catch (err) {
      toast.error("Erro ao remover defeito", { description: errorMessage(err) });
    }
  };

  // --- preview ---
  const [previewFabricId, setPreviewFabricId] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewHighlight, setPreviewHighlight] = useState(null);
  const openPreview = async (fabric) => {
    setPreviewFabricId(fabric.id);
    setPreviewData(null);
    setPreviewLoading(true);
    try {
      const res = await api.get(`/orders/${id}/fabrics/${fabric.id}/preview`);
      setPreviewData(res.data);
    } catch (err) {
      toast.error("Erro ao carregar desenho", { description: errorMessage(err) });
      setPreviewFabricId(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  // --- fabric file download / attach ---
  const downloadFabricFile = async (fabric) => {
    try {
      const res = await api.get(`/orders/${id}/fabrics/${fabric.id}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = fabric.file.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Erro ao descarregar ficheiro");
    }
  };
  const uploadFabricFile = async (fabricId, f) => {
    if (!f) return;
    const lower = f.name.toLowerCase();
    if (!lower.endsWith(".dxf") && !lower.endsWith(".gbr")) {
      toast.error("Formato inválido", { description: "Selecione um ficheiro .dxf ou .gbr." });
      return;
    }
    const fd = new FormData();
    fd.append("file", f);
    try {
      const res = await api.post(`/orders/${id}/fabrics/${fabricId}/file`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setOrder(res.data);
      toast.success("Ficheiro anexado");
    } catch (err) {
      toast.error("Erro ao anexar ficheiro", { description: errorMessage(err) });
    }
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  if (!order) return null;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="h-14 shrink-0 border-b border-border flex items-center justify-between px-4 bg-card gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={() => navigate("/ordens")} className="w-9 h-9 shrink-0 border border-border flex items-center justify-center hover:bg-accent transition-colors" title="Voltar às ordens">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="w-8 h-8 shrink-0 bg-foreground flex items-center justify-center">
            <ClipboardList className="w-4 h-4 text-background" />
          </div>
          <div className="leading-none min-w-0">
            <h1 className="font-head text-sm font-bold tracking-tight truncate">{order.client} · {order.reference}</h1>
            <p className="text-[10px] text-muted-foreground mono mt-0.5">{order.company_name}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button variant="outline" className="rounded-[4px]" onClick={openEdit} data-testid="order-edit-btn">
            <Pencil className="w-4 h-4" /> Editar
          </Button>
          <Button variant="outline" className="rounded-[4px] text-destructive" onClick={() => setDeleteOpen(true)} data-testid="order-delete-btn">
            <Trash2 className="w-4 h-4" />
          </Button>
          <button onClick={logout} title="Terminar sessão" className="w-9 h-9 border border-border flex items-center justify-center hover:bg-accent transition-colors">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </header>

      <main className="p-6 max-w-5xl mx-auto space-y-6">
        <div className="grid grid-cols-2 sm:grid-cols-4 border border-border bg-card">
          <MetaCell icon={Hash} label="Total peças" value={order.total_pieces} />
          <MetaCell icon={Scissors} label="Cortadas" value={order.cut_pieces} />
          <MetaCell icon={Percent} label="Progresso" value={fmtPct(order.progress_pct)} accent={order.progress_pct >= 100} />
          <MetaCell icon={AlertTriangle} label="Defeitos" value={order.defects_count} />
        </div>

        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Estado</span>
            <Badge variant="outline" className={ORDER_STATUS_META[order.status]?.className}>{ORDER_STATUS_META[order.status]?.label}</Badge>
            <span className="text-xs text-muted-foreground">· Prazo {order.due_date}</span>
          </div>
          <Button onClick={() => setAddFabricOpen(true)} className="rounded-[4px]" data-testid="add-fabric-btn">
            <Plus className="w-4 h-4" /> Adicionar tecido
          </Button>
        </div>

        {order.fabrics.length === 0 ? (
          <div className="border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
            Ainda sem tecidos. Adiciona o primeiro tecido/matriz a cortar.
          </div>
        ) : (
          <div className="space-y-4">
            {order.fabrics.map((fabric) => (
              <FabricCard
                key={fabric.id}
                fabric={fabric}
                onEditMatrix={openMatrix}
                onRegisterCut={openCut}
                onRegisterDefect={openDefect}
                onDeleteDefect={deleteDefect}
                onPreview={openPreview}
                onDeleteFabric={setDeleteFabricId}
                onStatusChange={changeFabricStatus}
                onDownload={downloadFabricFile}
                onAttachClick={(fabricId) => fileInputs.current[fabricId]?.click()}
                onFileChange={uploadFabricFile}
                registerFileInput={(el) => { fileInputs.current[fabric.id] = el; }}
              />
            ))}
          </div>
        )}
      </main>

      {/* --- edit order --- */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Editar ordem</DialogTitle></DialogHeader>
          {editForm && (
            <form onSubmit={saveEdit} className="space-y-4">
              <div className="space-y-1.5">
                <Label>Cliente</Label>
                <Input required value={editForm.client} onChange={(e) => setEditForm({ ...editForm, client: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Referência</Label>
                <Input required value={editForm.reference} onChange={(e) => setEditForm({ ...editForm, reference: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Prazo de entrega</Label>
                <Input required type="date" value={editForm.due_date} onChange={(e) => setEditForm({ ...editForm, due_date: e.target.value })} />
              </div>
              <DialogFooter>
                <Button type="submit" disabled={savingOrder} className="rounded-[4px]">
                  {savingOrder ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Guardar
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* --- add fabric --- */}
      <Dialog open={addFabricOpen} onOpenChange={(open) => (open ? setAddFabricOpen(true) : resetAddFabric())}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Adicionar tecido</DialogTitle></DialogHeader>
          <form onSubmit={submitAddFabric} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Nome do tecido</Label>
                <Input required autoFocus value={fabricName} onChange={(e) => setFabricName(e.target.value)} data-testid="fabric-name-input" />
              </div>
              {fabricFile && (
                <div className="space-y-1.5">
                  <Label>Nº de camadas</Label>
                  <Input required type="number" min={0} step={1} value={fabricLayers} onChange={(e) => setFabricLayers(e.target.value)} data-testid="fabric-layers-input" />
                </div>
              )}
            </div>

            <div className="space-y-1.5">
              <Label>Ficheiro do plano (.dxf/.gbr) — opcional, pode anexar depois</Label>
              <input ref={fabricFileInputRef} type="file" accept=".dxf,.gbr" className="hidden" onChange={(e) => pickFabricFile(e.target.files?.[0])} data-testid="fabric-file-input" />
              {fabricFile ? (
                <div className="flex items-center justify-between border border-border p-2 text-sm">
                  <span className="mono truncate">{fabricFile.name}</span>
                  <button type="button" onClick={removeFabricFile}><X className="w-4 h-4" /></button>
                </div>
              ) : (
                <div
                  onClick={() => fabricFileInputRef.current?.click()}
                  className="cursor-pointer border-2 border-dashed border-border p-4 text-center text-xs text-muted-foreground"
                >
                  <UploadCloud className="w-5 h-5 mx-auto mb-1" /> Arraste ou clique para carregar
                </div>
              )}
            </div>

            {parsingFabricFile && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> A ler o ficheiro e a preparar a matriz...
              </div>
            )}

            {fabricFile && !parsingFabricFile && (
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label>Matriz (modelo × tamanho → qtd por camada) — confirma ou ajusta</Label>
                  <Button type="button" size="sm" variant="outline" className="rounded-[4px]" onClick={addFabricMatrixRow}>
                    <Plus className="w-3.5 h-3.5" /> Linha
                  </Button>
                </div>
                <div className="border border-border">
                  <div className="grid grid-cols-[1fr_100px_120px_40px] text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border bg-muted/40">
                    <div className="px-2 py-1.5">Modelo</div>
                    <div className="px-2 py-1.5">Tamanho</div>
                    <div className="px-2 py-1.5">Qtd/camada</div>
                    <div></div>
                  </div>
                  {fabricMatrix.length === 0 ? (
                    <div className="px-3 py-4 text-xs text-muted-foreground text-center">
                      Não foi possível identificar modelos/tamanhos automaticamente. Adiciona linhas manualmente.
                    </div>
                  ) : (
                    fabricMatrix.map((row, idx) => (
                      <div key={idx} className="grid grid-cols-[1fr_100px_120px_40px] items-center border-b border-border/60 last:border-b-0">
                        <Input className="border-0 rounded-none h-9 focus-visible:ring-0" value={row.piece_name} onChange={(e) => updateFabricMatrixRow(idx, "piece_name", e.target.value)} placeholder="Modelo" />
                        <Input className="border-0 rounded-none h-9 focus-visible:ring-0" value={row.size} onChange={(e) => updateFabricMatrixRow(idx, "size", e.target.value)} placeholder="-" />
                        <Input className="border-0 rounded-none h-9 focus-visible:ring-0" type="number" min={0} step={1} value={row.qty_per_layer} onChange={(e) => updateFabricMatrixRow(idx, "qty_per_layer", e.target.value)} />
                        <button type="button" onClick={() => removeFabricMatrixRow(idx)} className="flex items-center justify-center h-9 text-muted-foreground hover:text-destructive">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))
                  )}
                </div>
                <div className="text-xs text-muted-foreground">
                  Total por camada: {fabricMatrix.reduce((s, r) => s + (Number(r.qty_per_layer) || 0), 0)} peças
                  {" · "}Total da produção: {fabricMatrix.reduce((s, r) => s + (Number(r.qty_per_layer) || 0), 0) * (Number(fabricLayers) || 0)} peças
                </div>
              </div>
            )}

            <DialogFooter>
              <Button type="submit" disabled={addingFabric || parsingFabricFile} className="rounded-[4px]" data-testid="fabric-create-submit">
                {addingFabric ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Adicionar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* --- edit matrix --- */}
      <Dialog open={!!matrixFabricId} onOpenChange={(open) => !open && closeMatrix()}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Matriz — {matrixForm?.name}</DialogTitle></DialogHeader>
          {matrixForm && (
            <form onSubmit={saveMatrix} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Nome do tecido</Label>
                  <Input required value={matrixForm.name} onChange={(e) => setMatrixForm({ ...matrixForm, name: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>Nº de camadas</Label>
                  <Input required type="number" min={0} step={1} value={matrixForm.layers} onChange={(e) => setMatrixForm({ ...matrixForm, layers: e.target.value })} data-testid="matrix-layers-input" />
                </div>
              </div>

              <div className="flex items-center justify-between flex-wrap gap-2">
                <Label>Matriz (modelo × tamanho → qtd por camada)</Label>
                <div className="flex gap-2">
                  {order.fabrics.find((f) => f.id === matrixFabricId)?.file && (
                    <Button type="button" size="sm" variant="outline" className="rounded-[4px]" onClick={resetFromFile} disabled={loadingSuggestion}>
                      {loadingSuggestion ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />} Repor sugestão do ficheiro
                    </Button>
                  )}
                  <Button type="button" size="sm" variant="outline" className="rounded-[4px]" onClick={addMatrixRow} data-testid="matrix-add-row">
                    <Plus className="w-3.5 h-3.5" /> Linha
                  </Button>
                </div>
              </div>

              <div className="border border-border">
                <div className="grid grid-cols-[1fr_100px_120px_40px] text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border bg-muted/40">
                  <div className="px-2 py-1.5">Modelo</div>
                  <div className="px-2 py-1.5">Tamanho</div>
                  <div className="px-2 py-1.5">Qtd/camada</div>
                  <div></div>
                </div>
                {matrixForm.matrix.length === 0 ? (
                  <div className="px-3 py-4 text-xs text-muted-foreground text-center">
                    Sem linhas. Adiciona uma linha ou repõe a sugestão do ficheiro.
                  </div>
                ) : (
                  matrixForm.matrix.map((row, idx) => (
                    <div key={idx} className="grid grid-cols-[1fr_100px_120px_40px] items-center border-b border-border/60 last:border-b-0">
                      <Input className="border-0 rounded-none h-9 focus-visible:ring-0" value={row.piece_name} onChange={(e) => updateMatrixRow(idx, "piece_name", e.target.value)} placeholder="Modelo" />
                      <Input className="border-0 rounded-none h-9 focus-visible:ring-0" value={row.size} onChange={(e) => updateMatrixRow(idx, "size", e.target.value)} placeholder="-" />
                      <Input className="border-0 rounded-none h-9 focus-visible:ring-0" type="number" min={0} step={1} value={row.qty_per_layer} onChange={(e) => updateMatrixRow(idx, "qty_per_layer", e.target.value)} />
                      <button type="button" onClick={() => removeMatrixRow(idx)} className="flex items-center justify-center h-9 text-muted-foreground hover:text-destructive">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))
                )}
              </div>

              <div className="text-xs text-muted-foreground">
                Total por camada: {matrixForm.matrix.reduce((s, r) => s + (Number(r.qty_per_layer) || 0), 0)} peças
                {" · "}Total da produção: {matrixForm.matrix.reduce((s, r) => s + (Number(r.qty_per_layer) || 0), 0) * (Number(matrixForm.layers) || 0)} peças
              </div>

              <DialogFooter>
                <Button type="submit" disabled={savingMatrix} className="rounded-[4px]" data-testid="matrix-save-btn">
                  {savingMatrix ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Guardar
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* --- register cut --- */}
      <Dialog open={!!cutFabricId} onOpenChange={(open) => !open && setCutFabricId(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Registar corte</DialogTitle></DialogHeader>
          <form onSubmit={submitCut} className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Quantidade cortada agora, por modelo/tamanho</Label>
              <Button type="button" size="sm" variant="outline" className="rounded-[4px]" onClick={fillAllCut} data-testid="cut-fill-all-btn">
                Tudo cortado
              </Button>
            </div>
            <div className="border border-border">
              <div className="grid grid-cols-[1fr_80px_70px_70px_100px] text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border bg-muted/40">
                <div className="px-2 py-1.5">Modelo</div>
                <div className="px-2 py-1.5">Tamanho</div>
                <div className="px-2 py-1.5 text-right">Já</div>
                <div className="px-2 py-1.5 text-right">Falta</div>
                <div className="px-2 py-1.5 text-right">Cortar agora</div>
              </div>
              {cutRows.map((row, idx) => (
                <div key={idx} className={`grid grid-cols-[1fr_80px_70px_70px_100px] items-center border-b border-border/60 last:border-b-0 ${row.remaining <= 0 ? "opacity-50" : ""}`}>
                  <div className="px-2 py-1.5 text-sm truncate">{row.piece_name}</div>
                  <div className="px-2 py-1.5 text-sm">{row.size}</div>
                  <div className="px-2 py-1.5 text-sm text-right mono">{row.already}</div>
                  <div className="px-2 py-1.5 text-sm text-right mono">{row.remaining}</div>
                  <Input
                    className="border-0 rounded-none h-9 text-right focus-visible:ring-0"
                    type="number"
                    min={0}
                    step={1}
                    value={row.value}
                    onChange={(e) => updateCutRow(idx, e.target.value)}
                    data-testid={`cut-row-${idx}`}
                  />
                </div>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground">Fica registado com o teu nome ({user?.name}) e a hora atual.</p>
            <DialogFooter>
              <Button type="submit" disabled={savingCut} className="rounded-[4px]" data-testid="cut-submit-btn">
                {savingCut ? <Loader2 className="w-4 h-4 animate-spin" /> : <Scissors className="w-4 h-4" />} Registar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* --- register defect --- */}
      <Dialog open={!!defectFabricId} onOpenChange={(open) => !open && setDefectFabricId(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Registar defeito</DialogTitle></DialogHeader>
          <form onSubmit={submitDefect} className="space-y-4">
            <div className="space-y-1.5">
              <Label>Tipo</Label>
              <Select value={defectForm.type} onValueChange={(v) => setDefectForm({ ...defectForm, type: v })}>
                <SelectTrigger data-testid="defect-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DEFECT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
              {defectForm.type === "Outro" && (
                <Input required placeholder="Descreve o tipo de defeito" value={defectForm.customType} onChange={(e) => setDefectForm({ ...defectForm, customType: e.target.value })} />
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Modelo (opcional)</Label>
                <Input value={defectForm.piece_name} onChange={(e) => setDefectForm({ ...defectForm, piece_name: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Tamanho (opcional)</Label>
                <Input value={defectForm.size} onChange={(e) => setDefectForm({ ...defectForm, size: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Quantidade</Label>
              <Input required type="number" min={1} step={1} value={defectForm.quantity} onChange={(e) => setDefectForm({ ...defectForm, quantity: e.target.value })} />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={savingDefect} className="rounded-[4px]" data-testid="defect-submit-btn">
                {savingDefect ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Registar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* --- preview --- */}
      <Dialog open={!!previewFabricId} onOpenChange={(open) => !open && setPreviewFabricId(null)}>
        <DialogContent className="max-w-4xl h-[80vh] flex flex-col">
          <DialogHeader><DialogTitle>Desenho do plano</DialogTitle></DialogHeader>
          <div className="flex-1 min-h-0 relative border border-border">
            {previewLoading || !previewData ? (
              <div className="h-full flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
            ) : (
              <DxfCanvas data={previewData} highlightKey={previewHighlight} setHighlightKey={setPreviewHighlight} showLabels={true} />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* --- delete order --- */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Apagar esta ordem de corte?</AlertDialogTitle>
            <AlertDialogDescription>
              Todos os tecidos, matrizes, ficheiros e histórico de corte associados também são apagados. Esta ação é irreversível.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteOrder} disabled={deleting} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Apagar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* --- delete fabric --- */}
      <AlertDialog open={!!deleteFabricId} onOpenChange={(open) => !open && setDeleteFabricId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover este tecido?</AlertDialogTitle>
            <AlertDialogDescription>
              O ficheiro e o histórico de corte associados também são apagados. Esta ação é irreversível.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteFabric} disabled={deletingFabric} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {deletingFabric ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Remover
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
